import type { MouseEvent, ReactNode } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import type { BlockContent } from "../../../lib/blockContentSchemas";
import { SampleArtwork } from "./SampleArtwork";

export type PageCardStyle = BlockContent<"page">["display"];

type PageCardProps = {
  variant: PageCardStyle;
  /** Smaller type and no description, for cards inside a library. */
  compact?: boolean;
  title: string;
  details: string;
  description: string | null;
  imageUrl: string | null;
  sample?: string;
  href?: string;
  onOpen?: () => void;
};

/** A link to a page, in one of three styles: Row, Feature or Index. */
export const PageCard = ({
  variant,
  compact = false,
  title,
  details,
  description,
  imageUrl,
  sample,
  href,
  onOpen,
}: PageCardProps) => {
  const media = (className: string) => (
    <PageCardMedia className={className} imageUrl={imageUrl} sample={sample} title={title} />
  );
  if (variant === "feature")
    return (
      <PageCardFrame className="flex flex-col" href={href} onOpen={onOpen}>
        {media(compact ? "aspect-[4/3] rounded-site" : "aspect-[2/1] rounded-site")}
        <span className={`site-heading block leading-tight ${compact ? "mt-2.5 heading-size-18" : "mt-3.5 heading-size-22"}`}>
          {title}
        </span>
        {details && <span className="mt-1 block site-label-12 leading-4 text-site-muted">{details}</span>}
        {!compact && description && (
          <span className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-site-muted">
            {description}
          </span>
        )}
        {!compact && (
          <span className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-medium text-site-accent">
            View <CardArrow />
          </span>
        )}
      </PageCardFrame>
    );
  if (variant === "link")
    return (
      <PageCardFrame
        className="flex items-baseline gap-4 border-b border-site-border py-4"
        href={href}
        onOpen={onOpen}
      >
        <span className={`site-heading min-w-0 flex-1 leading-snug wrap-break-word transition-colors group-hover:text-site-accent ${compact ? "heading-size-20" : "heading-size-22"}`}>
          {title}
        </span>
        {details && <span className="shrink-0 site-label-12 leading-4 text-site-muted">{details}</span>}
        <CardArrow icon={ArrowUpRight} />
      </PageCardFrame>
    );
  return (
    <PageCardFrame
      className="flex items-center gap-3.5 rounded-site border border-site-border bg-site-surface p-2.5 hover:border-site-accent-soft"
      href={href}
      onOpen={onOpen}
    >
      {media("size-16 shrink-0 rounded-site-sm [&_.sample-artwork_span]:text-sm")}
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] leading-snug font-medium wrap-break-word">{title}</span>
        {details && <span className="mt-0.5 block site-label-12 leading-4 text-site-muted">{details}</span>}
        {!compact && description && (
          <span className="mt-1 line-clamp-2 text-[13px] leading-snug text-site-muted">
            {description}
          </span>
        )}
      </span>
      <span className="mr-1.5">
        <CardArrow />
      </span>
    </PageCardFrame>
  );
};

const frameClass =
  "site-page-card group w-full touch-manipulation text-left text-site-text focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-site-accent";

/**
 * A link on the live site, a button in the editor. `site-page-card` marks it
 * for the editor's drag and touch handling.
 */
const PageCardFrame = ({
  className,
  href,
  onOpen,
  children,
}: {
  className: string;
  href?: string;
  onOpen?: () => void;
  children: ReactNode;
}) => {
  if (href)
    return (
      <a
        className={`${frameClass} ${className}`}
        href={href}
        onClick={
          onOpen
            ? (event: MouseEvent) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onOpen();
              }
            : undefined
        }
      >
        {children}
      </a>
    );
  return (
    <button
      className={`${frameClass} ${className}`}
      type="button"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.();
      }}
    >
      {children}
    </button>
  );
};

/**
 * The page's cover, a sample, or (with neither) its first letter set in the
 * site's heading font: a cover of its own, not a missing picture.
 */
const PageCardMedia = ({
  className,
  imageUrl,
  sample,
  title,
}: {
  className: string;
  imageUrl: string | null;
  sample?: string;
  title: string;
}) => (
  <span
    aria-hidden="true"
    className={`@container grid place-items-center overflow-hidden bg-site-highlight [&_.sample-artwork]:size-full [&_.sample-artwork]:rounded-none ${className}`}
  >
    {imageUrl ? (
      <img
        alt=""
        className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
        draggable={false}
        loading="lazy"
        src={imageUrl}
      />
    ) : sample ? (
      <SampleArtwork kind={sample} />
    ) : (
      <span className="site-heading text-[min(42cqw,8rem)] leading-none text-site-accent-soft transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:transition-none">
        {Array.from(title.trim())[0]?.toUpperCase() ?? ""}
      </span>
    )}
  </span>
);

const CardArrow = ({ icon: Icon = ArrowRight }: { icon?: typeof ArrowRight }) => (
  <Icon
    aria-hidden="true"
    size={16}
    className="site-card-arrow shrink-0 text-site-accent transition-[transform,opacity] group-hover:translate-x-0.5 motion-reduce:transition-none"
  />
);
